import type { CommandType, ReplayKind } from "../app/storeTypes.ts";

// ---------------------------------------------------------------------------
// Parameter definition types
// ---------------------------------------------------------------------------

export type ParamType = "float" | "int" | "bool" | "enum";
export type ParamInputMapping = "worldSpace" | "normalized" | "radial";

interface ParamDefBase {
  /** Stable key used in the params Record. */
  key: string;
  /** Human label for the redo panel and status overlay. */
  label: string;
  /** If true, mouse input maps to this param by default during modal. */
  primaryInput?: boolean;
  /**
   * If set, the redo panel groups this param under a foldout keyed by this
   * string. The foldout's header label and default-open state come from the
   * matching entry in `CommandMetadata.paramGroups`. Params that share a
   * group key render together; ungrouped params render at the top level.
   */
  group?: string;
  /**
   * If set, the param is only rendered in the redo panel when this predicate
   * returns true against the current params. Hidden params still receive
   * defaults from `getDefaultParams` and still participate in replay — they
   * just don't appear in the UI. Used for conditional sliders (e.g. show
   * offset sliders only when count >= 2).
   */
  visibleWhen?: (
    params: Readonly<Record<string, number | boolean | string>>,
  ) => boolean;
}

interface ScalarParamDefBase extends ParamDefBase {
  /**
   * How mouse input maps to this param:
   * - "worldSpace": dx * visibleHeight(camera, dist) / rect.height
   * - "normalized": dx / (rect.width * viewportFactor)
   * - "radial": sqrt(dx²+dy²) / (rect.width * viewportFactor)
   */
  inputMapping?: ParamInputMapping;
  /** For normalized/radial mapping, the denominator fraction (default 0.4). */
  viewportFactor?: number;
  /** Base value added before mouse delta (for example cuts start at 1). */
  baseValue?: number;
}

export interface FloatParamDef extends ScalarParamDefBase {
  type: "float";
  default: number;
  min: number;
  max: number;
  /** Step size for slider UI. */
  step?: number;
  /** Decimal places to display. */
  precision?: number;
  /**
   * When true, the slider's `min`, `max`, `default`, and `step` are
   * multipliers of the target's world-space bounding-box diagonal, not
   * absolute world units. The UI resolves them at render time so a
   * 5 cm screw and a 5 m archway each get a proportionate slider span.
   *
   * The stored parameter VALUE is still absolute world units — only the
   * slider range scales. Useful for world-space distances
   * (bevel/inset/extrude offsets, solidify thickness, etc.).
   */
  scaledByBounds?: boolean;
}

export interface IntParamDef extends ScalarParamDefBase {
  type: "int";
  default: number;
  min: number;
  max: number;
  /** If true, scroll wheel adjusts this param during modal. */
  scrollAdjust?: boolean;
}

export interface BoolParamDef extends ParamDefBase {
  type: "bool";
  default: boolean;
}

export interface EnumParamDef extends ParamDefBase {
  type: "enum";
  default: string;
  options: readonly { label: string; value: string }[];
}

export type ParamDef =
  | FloatParamDef
  | IntParamDef
  | BoolParamDef
  | EnumParamDef;
export type ScalarParamDef = FloatParamDef | IntParamDef;

// ---------------------------------------------------------------------------
// Command metadata
// ---------------------------------------------------------------------------

export type CommandOutputSelection =
  | "preserve"
  | "newVertices"
  | "newFacesAndVertices"
  | "newFacesAndVerticesWhenAvailable";

export type CommandActiveElementUsage =
  | "none"
  | "seedActiveEdge"
  | "seedActiveEdgeOrPick"
  | "orderedSelectionFromActiveVertex"
  | "seedBoundaryFromActiveElement";

export type CommandPreviewMode =
  | "none"
  | "transform"
  | "topology"
  | "topologyPick";

export interface CommandMetadata {
  /** Human label shown in viewport/status/redo UI. */
  label: string;
  /** Optional active hint shown while the command is running. */
  hint: string | null;
  /** Keyboard shortcuts to switch active value mode during modal: key → param key. */
  valueModeSwitchKeys?: Record<string, string>;
  /** Parameter definitions, in display order. */
  params: readonly ParamDef[];
  /** Which operator entry this modal command belongs to, if any. */
  operatorId: string | null;
  /** How the command previews while active. */
  previewMode: CommandPreviewMode;
  /** How post-op selection should be rebuilt from the topology result. */
  outputSelection: CommandOutputSelection;
  /** Whether the command consumes active edit elements deterministically. */
  activeElementUsage: CommandActiveElementUsage;
  /**
   * Which replay strategy the Adjust Last Operation panel should invoke when
   * this op's parameters are tweaked, or `null` when the op cannot be replayed.
   * The matching strategy in `replayStrategies.ts` is the single source of
   * truth for both pre-op capture and post-confirm replay — adding a new
   * adjustable op category means registering a new strategy here, not
   * branching control flow at call sites.
   */
  replayKind: ReplayKind | null;
  /**
   * Optional foldout headers for params with a `group` field. Order here
   * controls display order of the foldouts; ungrouped params always render
   * before any foldouts. Groups with no visible params (after `visibleWhen`
   * filtering) render no header at all.
   */
  paramGroups?: readonly {
    key: string;
    label: string;
    defaultOpen?: boolean;
  }[];
}

export const COMMAND_METADATA: Record<CommandType, CommandMetadata> = {
  translate: {
    label: "Grab",
    hint: null,
    params: [],
    operatorId: null,
    previewMode: "transform",
    outputSelection: "preserve",
    activeElementUsage: "none",
    replayKind: null,
  },
  rotate: {
    label: "Rotate",
    hint: null,
    params: [],
    operatorId: null,
    previewMode: "transform",
    outputSelection: "preserve",
    activeElementUsage: "none",
    replayKind: null,
  },
  scale: {
    label: "Scale",
    hint: null,
    params: [],
    operatorId: null,
    previewMode: "transform",
    outputSelection: "preserve",
    activeElementUsage: "none",
    replayKind: null,
  },
  extrude: {
    label: "Extrude Region",
    hint: "Drag to set extrude distance",
    params: [
      {
        key: "offset",
        label: "Offset",
        type: "float",
        default: 0,
        min: -2,
        max: 2,
        inputMapping: "worldSpace",
        primaryInput: true,
        precision: 4,
        step: 0.005,
        scaledByBounds: true,
      },
    ],
    operatorId: "extrude",
    previewMode: "topology",
    outputSelection: "newFacesAndVerticesWhenAvailable",
    activeElementUsage: "none",
    replayKind: "geometry-snapshot",
  },
  extrudeAlongNormals: {
    label: "Extrude Along Normals",
    hint: "Drag to set extrude distance",
    params: [
      {
        key: "offset",
        label: "Offset",
        type: "float",
        default: 0,
        min: -2,
        max: 2,
        inputMapping: "worldSpace",
        primaryInput: true,
        precision: 4,
        step: 0.005,
        scaledByBounds: true,
      },
    ],
    operatorId: "extrude_along_normals",
    previewMode: "topology",
    outputSelection: "newFacesAndVerticesWhenAvailable",
    activeElementUsage: "none",
    replayKind: "geometry-snapshot",
  },
  extrudeIndividual: {
    label: "Extrude Individual Faces",
    hint: "Drag to set extrude distance",
    params: [
      {
        key: "offset",
        label: "Offset",
        type: "float",
        default: 0,
        min: -2,
        max: 2,
        inputMapping: "worldSpace",
        primaryInput: true,
        precision: 4,
        step: 0.005,
        scaledByBounds: true,
      },
    ],
    operatorId: "extrude_individual",
    previewMode: "topology",
    outputSelection: "newFacesAndVerticesWhenAvailable",
    activeElementUsage: "none",
    replayKind: "geometry-snapshot",
  },
  extrudeManifold: {
    label: "Extrude Manifold",
    hint: "Drag to carve into adjacent geometry",
    params: [
      {
        key: "offset",
        label: "Offset",
        type: "float",
        default: 0,
        min: -2,
        max: 2,
        inputMapping: "worldSpace",
        primaryInput: true,
        precision: 4,
        step: 0.005,
        scaledByBounds: true,
      },
    ],
    operatorId: "extrude_manifold",
    previewMode: "topology",
    outputSelection: "newFacesAndVerticesWhenAvailable",
    activeElementUsage: "none",
    replayKind: "geometry-snapshot",
  },
  subdivide: {
    label: "Subdivide",
    hint: "Drag or scroll to set cut count",
    params: [
      {
        key: "cuts",
        label: "Cuts",
        type: "int",
        default: 1,
        min: 1,
        max: 10,
        inputMapping: "normalized",
        viewportFactor: 0.3,
        baseValue: 1,
        primaryInput: true,
        scrollAdjust: true,
      },
    ],
    operatorId: "subdivide",
    previewMode: "topology",
    outputSelection: "newFacesAndVerticesWhenAvailable",
    activeElementUsage: "none",
    replayKind: "geometry-snapshot",
  },
  inset: {
    label: "Inset Faces",
    hint: "Drag to set inset thickness",
    params: [
      {
        key: "thickness",
        label: "Thickness",
        type: "float",
        default: 0,
        min: 0,
        max: 0.5,
        inputMapping: "radial",
        viewportFactor: 0.4,
        primaryInput: true,
        precision: 3,
        step: 0.002,
        scaledByBounds: true,
      },
      {
        key: "depth",
        label: "Depth",
        type: "float",
        default: 0,
        min: -0.5,
        max: 0.5,
        inputMapping: "worldSpace",
        precision: 4,
        step: 0.002,
        scaledByBounds: true,
      },
      { key: "individual", label: "Individual", type: "bool", default: false },
      { key: "outset", label: "Outset", type: "bool", default: false },
      { key: "evenOffset", label: "Even Offset", type: "bool", default: true },
      { key: "markSharp", label: "Mark Sharp", type: "bool", default: false },
      { key: "markSeam", label: "Mark Seam", type: "bool", default: false },
    ],
    valueModeSwitchKeys: { d: "depth" },
    operatorId: "inset_faces",
    previewMode: "topology",
    outputSelection: "newFacesAndVertices",
    activeElementUsage: "none",
    replayKind: "geometry-snapshot",
  },
  bevel: {
    label: "Bevel",
    hint: "Drag to set bevel width",
    params: [
      {
        key: "offset",
        label: "Offset",
        type: "float",
        default: 0,
        min: 0,
        max: 0.5,
        inputMapping: "worldSpace",
        primaryInput: true,
        precision: 4,
        step: 0.002,
        scaledByBounds: true,
      },
      {
        key: "segments",
        label: "Segments",
        type: "int",
        default: 1,
        min: 1,
        max: 16,
        scrollAdjust: true,
      },
      {
        key: "profile",
        label: "Profile",
        type: "float",
        default: 0.5,
        min: 0,
        max: 1,
        inputMapping: "normalized",
        viewportFactor: 0.4,
        precision: 2,
        step: 0.05,
      },
      {
        key: "clampOverlap",
        label: "Clamp Overlap",
        type: "bool",
        default: true,
      },
      { key: "markSharp", label: "Mark Sharp", type: "bool", default: true },
      { key: "markSeam", label: "Mark Seam", type: "bool", default: false },
    ],
    valueModeSwitchKeys: { p: "profile" },
    operatorId: "bevel",
    previewMode: "topology",
    outputSelection: "newVertices",
    activeElementUsage: "none",
    replayKind: "geometry-snapshot",
  },
  bridgeEdgeLoops: {
    label: "Bridge Edge Loops",
    hint: "Drag or scroll to twist loop pairing",
    params: [
      {
        key: "twist",
        label: "Twist",
        type: "int",
        default: 0,
        min: -256,
        max: 256,
        inputMapping: "normalized",
        viewportFactor: 0.3,
        baseValue: 0,
        primaryInput: true,
        scrollAdjust: true,
      },
      { key: "reverse", label: "Reverse", type: "bool", default: false },
    ],
    operatorId: "bridge_edge_loops",
    previewMode: "topology",
    outputSelection: "newFacesAndVertices",
    activeElementUsage: "seedActiveEdge",
    replayKind: "geometry-snapshot",
  },
  gridFill: {
    label: "Grid Fill",
    hint: "Drag or scroll to set row count",
    params: [
      {
        key: "span",
        label: "Span",
        type: "int",
        default: 1,
        min: 1,
        max: 64,
        inputMapping: "normalized",
        viewportFactor: 0.3,
        baseValue: 1,
        primaryInput: true,
        scrollAdjust: true,
      },
      {
        key: "offset",
        label: "Offset",
        type: "int",
        default: 0,
        min: -256,
        max: 256,
        scrollAdjust: true,
      },
    ],
    valueModeSwitchKeys: { o: "offset" },
    operatorId: "grid_fill",
    previewMode: "topology",
    outputSelection: "newFacesAndVertices",
    activeElementUsage: "seedBoundaryFromActiveElement",
    replayKind: "geometry-snapshot",
  },
  mergeByDistance: {
    label: "Merge by Distance",
    hint: "Drag to set merge threshold",
    params: [
      {
        key: "threshold",
        label: "Threshold",
        type: "float",
        default: 0.01,
        min: 0.00001,
        max: 0.1,
        inputMapping: "worldSpace",
        primaryInput: true,
        precision: 4,
        step: 0.0001,
        scaledByBounds: true,
      },
    ],
    operatorId: null,
    previewMode: "topology",
    outputSelection: "newVertices",
    activeElementUsage: "none",
    replayKind: "geometry-snapshot",
  },
  edgeSlide: {
    label: "Edge Slide",
    hint: "Drag to slide along edges",
    params: [],
    operatorId: "edge_slide",
    previewMode: "transform",
    outputSelection: "preserve",
    activeElementUsage: "none",
    replayKind: null,
  },
  edgeLoopInsert: {
    label: "Loop Cut",
    hint: "Click an edge to place the cut",
    params: [
      {
        key: "position",
        label: "Position",
        type: "float",
        default: 0.5,
        min: 0.01,
        max: 0.99,
        inputMapping: "normalized",
        viewportFactor: 0.4,
        baseValue: 0.5,
        primaryInput: true,
        precision: 3,
        step: 0.01,
      },
      {
        key: "cuts",
        label: "Cuts",
        type: "int",
        default: 1,
        min: 1,
        max: 10,
        scrollAdjust: true,
      },
    ],
    operatorId: "insert_edge_loop",
    previewMode: "topologyPick",
    outputSelection: "newVertices",
    activeElementUsage: "seedActiveEdgeOrPick",
    replayKind: "geometry-snapshot",
  },
  shrinkFatten: {
    label: "Shrink/Fatten",
    hint: "Drag to offset along normals",
    params: [
      {
        key: "offset",
        label: "Offset",
        type: "float",
        default: 0,
        min: -0.5,
        max: 0.5,
        inputMapping: "worldSpace",
        primaryInput: true,
        precision: 4,
        step: 0.002,
        scaledByBounds: true,
      },
    ],
    operatorId: "shrink_fatten",
    previewMode: "transform",
    outputSelection: "preserve",
    activeElementUsage: "none",
    replayKind: null,
  },
  skinRadius: {
    label: "Skin Radius",
    hint: "Drag to scale tube radius",
    params: [
      {
        key: "factor",
        label: "Radius",
        type: "float",
        default: 1,
        min: 0,
        max: 10,
        inputMapping: "worldSpace",
        primaryInput: true,
        precision: 4,
        step: 0.01,
      },
    ],
    operatorId: "skin_radius",
    previewMode: "transform",
    outputSelection: "preserve",
    activeElementUsage: "none",
    replayKind: null,
  },
  boneRotate: {
    label: "Bone Rotate",
    hint: null,
    params: [],
    operatorId: null,
    previewMode: "transform",
    outputSelection: "preserve",
    activeElementUsage: "none",
    replayKind: null,
  },
  boneTranslate: {
    label: "Bone IK",
    hint: "Drag to move end effector",
    params: [],
    operatorId: null,
    previewMode: "transform",
    outputSelection: "preserve",
    activeElementUsage: "none",
    replayKind: null,
  },
  cursorTranslate: {
    label: "Cursor",
    hint: null,
    params: [],
    operatorId: null,
    previewMode: "transform",
    outputSelection: "preserve",
    activeElementUsage: "none",
    replayKind: null,
  },
  selectSimilarNormal: {
    label: "Select Similar: Normal",
    hint: null,
    params: [
      {
        key: "threshold",
        label: "Angle",
        type: "float",
        default: 0,
        min: 0,
        max: 180,
        step: 1,
        precision: 1,
      },
    ],
    operatorId: null,
    previewMode: "none",
    outputSelection: "preserve",
    activeElementUsage: "none",
    replayKind: "selection-seed",
  },
  surfaceBox: {
    label: "Surface Box",
    hint: null,
    params: [],
    operatorId: "surface_box",
    previewMode: "none",
    outputSelection: "preserve",
    activeElementUsage: "none",
    replayKind: null,
  },
  surfaceCylinder: {
    label: "Surface Cylinder",
    hint: null,
    params: [],
    operatorId: "surface_cylinder",
    previewMode: "none",
    outputSelection: "preserve",
    activeElementUsage: "none",
    replayKind: null,
  },
  surfacePoly: {
    label: "Surface Poly",
    hint: null,
    params: [],
    operatorId: "surface_poly",
    previewMode: "none",
    outputSelection: "preserve",
    activeElementUsage: "none",
    replayKind: null,
  },
};

const OPERATOR_COMMAND_TYPES = new Map<string, CommandType>();
for (const [type, meta] of Object.entries(COMMAND_METADATA) as [
  CommandType,
  CommandMetadata,
][]) {
  if (meta.operatorId) OPERATOR_COMMAND_TYPES.set(meta.operatorId, type);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function getCommandMetadata(type: CommandType): CommandMetadata {
  return COMMAND_METADATA[type];
}

export function getCommandTypeForOperator(
  operatorId: string,
): CommandType | null {
  return OPERATOR_COMMAND_TYPES.get(operatorId) ?? null;
}

export function isOperatorActiveFromCommand(
  operatorId: string,
  type: CommandType,
): boolean {
  return OPERATOR_COMMAND_TYPES.get(operatorId) === type;
}

/** Get default params for a command type. Returns empty record for unregistered types. */
export function getDefaultParams(
  type: CommandType,
): Record<string, number | boolean | string> {
  const meta = COMMAND_METADATA[type];
  const result: Record<string, number | boolean | string> = {};
  for (const p of meta.params) result[p.key] = p.default;
  return result;
}

/** Find the primary input param def for a command type. */
export function getPrimaryParam(type: CommandType): ParamDef | null {
  const meta = COMMAND_METADATA[type];
  return meta.params.find((p) => p.primaryInput) ?? meta.params[0] ?? null;
}

/** Find a param definition by key. */
export function getParamDef(type: CommandType, key: string): ParamDef | null {
  return COMMAND_METADATA[type].params.find((p) => p.key === key) ?? null;
}

/** Find the scroll-adjustable int param for a command type. */
export function getScrollParam(type: CommandType): IntParamDef | null {
  const found = COMMAND_METADATA[type].params.find(
    (p): p is IntParamDef => p.type === "int" && !!p.scrollAdjust,
  );
  return found ?? null;
}

/**
 * Resolve the effective (min, max, step, default) for a float param,
 * folding in the target's world-space diagonal when `scaledByBounds` is
 * set. Passing a diagonal of `1` (or omitting it) yields the raw static
 * values.
 */
export function resolveFloatRange(
  param: FloatParamDef,
  diagonal: number = 1,
): { min: number; max: number; step: number; default: number } {
  const k = param.scaledByBounds ? (diagonal > 0 ? diagonal : 1) : 1;
  return {
    min: param.min * k,
    max: param.max * k,
    step: (param.step ?? 0.01) * (param.scaledByBounds ? k : 1),
    default: param.default * k,
  };
}

/** True iff the command type previews through snapshot-based topology execution. */
export function usesTopologyPreview(type: CommandType): boolean {
  const previewMode = COMMAND_METADATA[type].previewMode;
  return previewMode === "topology" || previewMode === "topologyPick";
}

/** True iff the command type starts in pick mode before topology preview begins. */
export function usesTopologyPickPreview(type: CommandType): boolean {
  return COMMAND_METADATA[type].previewMode === "topologyPick";
}

/** True iff the command type has a registered replay strategy. */
export function supportsAdjustLastOperation(type: CommandType): boolean {
  return COMMAND_METADATA[type].replayKind !== null;
}
